import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {exportProductCardAuditEvent, fetchProductCardAuditEvent} from 'api/productCardAudit';
import {ReactComponent as DownloadIcon} from 'assets/svg/download.svg';
import {AuditGeneralInformation} from 'components/common/AuditLog/GeneralInformation';
import {
    DetailCard,
    DetailHeader,
    DetailHeaderActions,
    DetailId,
    DetailTitle,
    EmptyDetail,
    Field,
    FieldLabel,
    FieldsGrid,
    FieldValue,
    Section,
    SectionTitle,
} from 'components/common/AuditLog/styled';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {formatLocalDateTime} from 'components/pages/RecallAudit/utils';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {Spinner} from 'components/ui-kit/Spinner';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {ProductCardAuditEntry} from 'types/product-card-audit';

import {ProductCardAuditErrorsModal} from './ErrorsModal';
import {ChangesTable, EventBadge} from './styled';

export const ProductCardAuditDetailPage = () => {
    const {id: eventId} = useParams<{id: string}>();
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    const [entry, setEntry] = useState<ProductCardAuditEntry | null>(null);
    const [pending, setPending] = useState(true);
    const [exportPending, setExportPending] = useState(false);
    const [errorsOpen, setErrorsOpen] = useState(false);
    useEffect(() => {
        let active = true;
        setEntry(null);
        setPending(true);
        fetchProductCardAuditEvent(eventId)
            .then((value) => {
                if (active) setEntry(value);
            })
            .catch((error) => {
                if (active) showRestError(error as RestError);
            })
            .finally(() => {
                if (active) setPending(false);
            });
        return () => {
            active = false;
        };
    }, [eventId]);
    const exportPdf = async () => {
        if (exportPending) return;
        setExportPending(true);
        try {
            saveAs(await exportProductCardAuditEvent(eventId), `product-card-audit-${eventId}.pdf`);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setExportPending(false);
        }
    };
    const field = (
        key: keyof ProductCardAuditEntry,
        value: React.ReactNode = entry?.[key] as React.ReactNode,
    ) => (
        <Field key={key} wide={key === 'rejectionReason'}>
            <FieldLabel>{t(`pages.productCardAudit.fields.${key}`)}</FieldLabel>
            <FieldValue>{value != null && value !== '' ? value : '—'}</FieldValue>
        </Field>
    );
    const isBulk = entry?.eventType === 'PRODUCT_CARD_BULK_UPLOAD_COMPLETED';
    return (
        <Page title={t('pages.recallAudit.detail.title')}>
            <BackTo to={ROUTE.PRODUCT_CARD_AUDIT}>{t('pages.recallAudit.detail.back')}</BackTo>
            {pending ? (
                <EmptyDetail>
                    <Spinner />
                </EmptyDetail>
            ) : !entry ? (
                <EmptyDetail>{t('pages.recallAudit.detail.notFound')}</EmptyDetail>
            ) : (
                <DetailCard>
                    <DetailHeader>
                        <div>
                            <DetailTitle>{t('pages.recallAudit.detail.title')}</DetailTitle>
                            <DetailId>ID: {entry.eventId}</DetailId>
                        </div>
                        <DetailHeaderActions>
                            <EventBadge>
                                {t(`pages.productCardAudit.events.${entry.eventType}`, {
                                    defaultValue: entry.eventType,
                                })}
                            </EventBadge>
                            {permissions.includes(PERMISSION_ALIASES.productCardAuditLogExport) && (
                                <Button
                                    size="sm"
                                    variant={ButtonVariant.Secondary}
                                    disabled={exportPending}
                                    isLoading={exportPending}
                                    onClick={exportPdf}
                                    icon={<Icon source={DownloadIcon} width={16} height={16} />}
                                >
                                    {t('pages.recallAudit.export.pdf')}
                                </Button>
                            )}
                        </DetailHeaderActions>
                    </DetailHeader>
                    {!isBulk && <AuditGeneralInformation entry={entry} />}
                    <Section>
                        <SectionTitle>
                            {t(
                                `pages.productCardAudit.${
                                    isBulk ? 'bulkGeneral' : 'productDetails'
                                }`,
                            )}
                        </SectionTitle>
                        <FieldsGrid>
                            {isBulk ? (
                                <>
                                    {field('fileName')}
                                    {field('organizationId')}
                                    {field('createdCount')}
                                    {field('failedCount')}
                                    {field('userFullName')}
                                </>
                            ) : (
                                <>
                                    {field('productCode')}
                                    {field('productId')}
                                    {field('productVersion')}
                                    {[
                                        'PRODUCT_CARD_VERSION_CREATED',
                                        'PRODUCT_CARD_PUBLISHED',
                                    ].includes(entry.eventType) &&
                                        field(
                                            'previousVersionId',
                                            entry.previousVersionId && (
                                                <Link
                                                    to={`${
                                                        ROUTE.PRODUCT_CARD_AUDIT
                                                    }?productId=${encodeURIComponent(
                                                        entry.previousVersionId,
                                                    )}`}
                                                >
                                                    {entry.previousVersionId}
                                                </Link>
                                            ),
                                        )}
                                    {entry.eventType === 'PRODUCT_CARD_REJECTED' &&
                                        field('rejectionReason')}
                                    {[
                                        'PRODUCT_CARD_DECLARATION_SIGNED',
                                        'PRODUCT_CARD_SUBMITTED',
                                    ].includes(entry.eventType) && field('declarationId')}
                                    {entry.eventType === 'PRODUCT_CARD_DECLARATION_SIGNED' && (
                                        <>
                                            {field('declarationType')}
                                            {field('declarationVersion')}
                                            {field(
                                                'signedAt',
                                                entry.signedAt
                                                    ? formatLocalDateTime(entry.signedAt)
                                                    : '—',
                                            )}
                                            {field('declarationHash')}
                                            {field('declarationPdfHash')}
                                        </>
                                    )}
                                </>
                            )}
                        </FieldsGrid>
                    </Section>
                    {isBulk && (
                        <Section>
                            <SectionTitle>{t('pages.productCardAudit.bulkDetails')}</SectionTitle>
                            <ChangesTable>
                                <thead>
                                    <tr>
                                        <th>{t('pages.recallAudit.detail.fullName')}</th>
                                        <th>{t('pages.productCardAudit.fields.fileName')}</th>
                                        <th>{t('pages.recallAudit.detail.dateTime')}</th>
                                        <th>{t('pages.productCardAudit.loadStatus')}</th>
                                        <th>{t('pages.productCardAudit.listOfErrors')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{entry.userFullName || '—'}</td>
                                        <td>{entry.fileName || '—'}</td>
                                        <td>{formatLocalDateTime(entry.eventTimestamp)}</td>
                                        <td>
                                            {entry.loadStatus
                                                ? t(
                                                      `pages.productCardAudit.loadStatuses.${entry.loadStatus}`,
                                                  )
                                                : '—'}
                                        </td>
                                        <td>
                                            {entry.failedCount || entry.loadStatus === 'FAILED' ? (
                                                <Button
                                                    size="sm"
                                                    variant={ButtonVariant.Secondary}
                                                    onClick={() => setErrorsOpen(true)}
                                                >
                                                    {t('pages.productCardAudit.listOfErrors')}
                                                </Button>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </ChangesTable>
                        </Section>
                    )}
                    {entry.eventType === 'PRODUCT_CARD_UPDATED' && !!entry.changedFields?.length && (
                        <Section>
                            <SectionTitle>{t('pages.productCardAudit.changes')}</SectionTitle>
                            <ChangesTable>
                                <thead>
                                    <tr>
                                        {['field', 'before', 'after'].map((key) => (
                                            <th key={key}>{t(`pages.productCardAudit.${key}`)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {entry.changedFields.map((change, index) => (
                                        <tr key={`${change.field}-${index}`}>
                                            <td>
                                                {t(
                                                    `pages.productCardAudit.changedFields.${change.field}`,
                                                    {defaultValue: change.field},
                                                )}
                                            </td>
                                            <td>{change.oldValue ?? '—'}</td>
                                            <td>{change.newValue ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </ChangesTable>
                        </Section>
                    )}
                </DetailCard>
            )}
            {errorsOpen && (
                <ProductCardAuditErrorsModal
                    eventId={eventId}
                    onClose={() => setErrorsOpen(false)}
                />
            )}
        </Page>
    );
};
